'use client';
import { useState, useRef, useEffect } from 'react';
import AgGrid, { StatusCell, ClickableCell } from '../common/AgGrid';
import CommonModal from '../common/CommonModal';
import Icon from '../common/Icon';
import {
  GetClientDetails,
  GetManagedClients,
  GetAllUsersInternalRoles,
  GetAccountingType,
  CreateClients,
  UpdateClientById,
  GetStateType,
  GetClientById,
  DeleteClientById
} from '../../services/accountService';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import SuccessMessage from '../common/SuccessMessage';

const ClientMaintenance = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [modalMode, setModalMode] = useState('add');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [clientIds, setClientIds] = useState(new Set());
  const [clientAbbreviations, setClientAbbreviations] = useState(new Set());
  const gridRef = useRef(null);


  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);


  const [searchText, setSearchText] = useState('');

 
  const [messageState, setMessageState] = useState({
    message: '',
    type: 'success' // 'success' or 'error'
  });

  const showSuccessMessage = (message) => {
    setMessageState({ message, type: 'success' });
  };

  const showErrorMessage = (message) => {
    setMessageState({ message, type: 'error' });
  };

  const handleCloseMessage = () => {
    setMessageState({ message: '', type: 'success' });
  };
 
  const [rowData, setRowData] = useState([]);

  
  const [accountManagers, setAccountManagers] = useState([
    { value: '', label: 'Select Account Manager', disabled: true }
  ]);

  const [productManagers, setProductManagers] = useState([
    { value: '', label: 'Select Product Manager', disabled: true }
  ]);

  const [listManagers, setListManagers] = useState([
    { value: '', label: 'Select List Manager', disabled: true }
  ]);

  const [consultants, setConsultants] = useState([
    { value: '', label: 'Select Consultant', disabled: true }
  ]);

  const [creativeDirector, setCreativeDirector] = useState([
    { value: '', label: 'Select Creative Director', disabled: true }
  ]);

  const [accountingTypes, setAccountingTypes] = useState([
    { value: '', label: 'Select Accounting Type', disabled: true }
  ]);

  const [managedClientTypes, setManagedClientTypes] = useState([
    { value: '', label: 'Please select', disabled: true }
  ]);

  const [stateTypes, setStateTypes] = useState([]);
  const [stateOptions, setStateOptions] = useState([
    { value: '', label: 'Loading states...', disabled: true }
  ]);

  const currentUser = {
    userID: 1,
    userName: 'Admin',
    role: 'Admin'
  };

  const [actionMenu, setActionMenu] = useState({
    isOpen: false,
    clientId: null,
    clientData: null,
    position: { x: 0, y: 0 }
  });
  const actionMenuRef = useRef(null);

  
  const suppressNextAutoCloseRef = useRef(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setActionMenu({ isOpen: false, clientId: null, clientData: null, position: { x: 0, y: 0 } });
      }
    };

    if (actionMenu.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenu.isOpen]);

  const mapStateIDToAbbreviation = (stateID) => {
    if (!stateID || !stateTypes.length) return '';
    const state = stateTypes.find(s => s.stateID === parseInt(stateID));
    return state ? (state.stateAbbreviation || state.abbreviation || state.stateCode || '') : '';
  };

  const mapAbbreviationToStateID = (abbreviation) => {
    if (!abbreviation || !stateTypes.length) return null;
    const state = stateTypes.find(s =>
      s.stateAbbreviation === abbreviation ||
      s.abbreviation === abbreviation ||
      s.stateCode === abbreviation
    );
    return state ? state.stateID : null;
  };

  const fetchStates = async () => {
    try {
      const statesResponse = await GetStateType();
      let statesData = [];

      if (statesResponse && statesResponse.success === true && Array.isArray(statesResponse.data)) {
        statesData = statesResponse.data;
      } else if (Array.isArray(statesResponse)) {
        statesData = statesResponse;
      } else if (statesResponse && Array.isArray(statesResponse.states)) {
        statesData = statesResponse.states;
      }

      if (statesData.length > 0) {
        setStateTypes(statesData);
        const mappedOptions = mapStateTypesToOptions(statesData);
        setStateOptions(mappedOptions);
        return statesData;
      } else {
        setStateOptions([{ value: '', label: 'No states available', disabled: true }]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStateOptions([{ value: '', label: 'Failed to load states', disabled: true }]);
      return [];
    }
  };

  const mapStateTypesToOptions = (states) => {
    if (!states || !Array.isArray(states) || states.length === 0) {
      return [{ value: '', label: 'No states available', disabled: true }];
    }

    const sortedStates = [...states].sort((a, b) => {
      const nameA = (a.stateName || a.name || '').toLowerCase();
      const nameB = (b.stateName || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const options = [
      { value: '', label: 'Select State', disabled: true },
      ...sortedStates.map(state => {
        const stateID = state.stateID || state.id || '';
        const stateName = state.stateName || state.name || '';
        const stateAbbreviation = state.stateAbbreviation || state.abbreviation || state.stateCode || '';

        return {
          value: stateAbbreviation,
          label: `${stateName} (${stateAbbreviation})`,
          disabled: false,
          metadata: {
            stateID: stateID,
            stateAbbreviation: stateAbbreviation
          }
        };
      })
    ];

    return options;
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      await fetchStates();

      const [clientsResponse, managedClientsResponse, usersResponse, accountingTypesResponse] = await Promise.all([
        GetClientDetails(),
        GetManagedClients(),
        GetAllUsersInternalRoles(),
        GetAccountingType()
      ]);

      const transformedData = Array.isArray(clientsResponse)
        ? clientsResponse.map((client) => transformClientData(client))
        : [];

      setRowData(transformedData);

      const existingIds = new Set();
      const existingAbbreviations = new Set();

      transformedData.forEach(client => {
        if (client.id) existingIds.add(client.id);
        if (client.abbreviation) existingAbbreviations.add(client.abbreviation);
      });

      setClientIds(existingIds);
      setClientAbbreviations(existingAbbreviations);

      const transformedManagedClients = [
        { value: '', label: 'Please select', disabled: true },
        ...(Array.isArray(managedClientsResponse) ? managedClientsResponse.map(clientType => ({
          value: clientType.clientTypeID?.toString() || '',
          label: clientType.clientTypeName || `Type ${clientType.clientTypeID}`,
          disabled: false
        })) : [])
      ];

      setManagedClientTypes(transformedManagedClients);

      const transformedUsers = [
        { value: '', label: 'Please select', disabled: true },
        ...(Array.isArray(usersResponse) ? usersResponse.map(user => ({
          value: user.userID?.toString() || '',
          label: user.userName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
          disabled: false
        })) : [])
      ];

      setAccountManagers(transformedUsers);
      setProductManagers(transformedUsers);
      setListManagers(transformedUsers);
      setConsultants(transformedUsers);
      setCreativeDirector(transformedUsers);

      const transformedAccountingTypes = [
        { value: '', label: 'Select Accounting Type', disabled: true },
        ...(Array.isArray(accountingTypesResponse) ? accountingTypesResponse.map(accountingType => ({
          value: accountingType.accountingTypeID?.toString() || '',
          label: accountingType.accountingTypeName || accountingType.name || '',
          disabled: false
        })) : [])
      ];

      setAccountingTypes(transformedAccountingTypes);

    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load client data. Please try again.');

      setRowData([]);
      setManagedClientTypes([
        { value: '', label: 'Please select', disabled: true },
        { value: '1', label: 'Attorney General' },
        { value: '2', label: 'C3 Nonprofit' },
        { value: '3', label: 'C4 Nonprofit' },
        { value: '4', label: 'Corporation' },
        { value: '5', label: 'Governor' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to transform client data (grid row)
  const transformClientData = (client) => {
    const primaryAddress = client.addresses?.find(addr => addr.addressTypeID === 3);
    const cagingAddress = client.addresses?.find(addr => addr.addressTypeID === 2);
    const breAddress = client.addresses?.find(addr => addr.addressTypeID === 1);

    return {
      id: client.clientID,
      clientId: client.clientID,
      Id: `${client.clientID || ''}`,
      clientNumber: client.clientCode || 'N/A',
      clientName: client.clientName || 'N/A',
      clientType: client.clientTypeName || 'N/A',
      clientTypeID: client.clientTypeID,
      status: client.isActive ? 'Active' : 'Inactive',
      email: client.clientAddress || '',
      active: client.isActive,
      isThankYouProgramActive: client.isThankYouProgramActive,
      // NEW: surface clientContractInfo on the row so it persists if needed
      clientContractInfo: client.clientContractInfo || '',
      abbreviation: client.clientCode || '',
      clientAddress: primaryAddress?.addressLine || '',
      city: primaryAddress?.city || '',
      state: mapStateIDToAbbreviation(primaryAddress?.stateID) || '',
      zip: primaryAddress?.zipCode || '',
      phone: client.clientPhoneNumber || '',
      organizationName: client.cagingOrgName || '',
      poBoxNumber: cagingAddress?.poBoxAddress || '',
      cagingCity: cagingAddress?.city || '',
      cagingState: mapStateIDToAbbreviation(cagingAddress?.stateID) || '',
      cagingZip: cagingAddress?.zipCode || '',
      breOrganizationName: client.breOrgName || '',
      brePoBoxNumber: breAddress?.poBoxAddress || '',
      breCity: breAddress?.city || '',
      breState: mapStateIDToAbbreviation(breAddress?.stateID) || '',
      breZip: breAddress?.zipCode || '',
      npAuthorizationNumber: client.npAuthorizationNumber || '',
      crid: client.crid || '',
      mid: client.mid || '',
      permitNumber: client.permitNumber || '',
      accountManager: client.accountManagerID?.toString() || '',
      productManager: client.productionManagerID?.toString() || '',
      listManager: client.listManagerID?.toString() || '',
      consultant: client.consultantID?.toString() || '',
      creativeDirector: client.creativeDirectorID?.toString() || '',
      accountingtype: client.accountingDetail?.accountingTypeID?.toString() || '',
      creativefee: client.accountingDetail?.creativeFee?.toString() || '',
      housefee: client.accountingDetail?.houseFee?.toString() || '',
      salesCommission: client.accountingDetail?.salesCommission || '',
      flatfee: client.accountingDetail?.flatFee?.toString() || '',
      monthlyRetainer: client.accountingDetail?.monthlyRetainer?.toString() || '',
      quantityPerPrice: client.accountingDetail?.quantityPerPiece?.toString() || '',
      sageClientCode: client.accountingDetail?.sageClientCode || '',
      designFee: client.accountingDetail?.designFee?.toString() || '',
      // ✅ NEW: contractDate surfaced on the grid row too (kept as raw ISO
      // string here; the edit form does the YYYY-MM-DD trim itself)
      contractDate: client.accountingDetail?.contractDate || ''
    };
  };

  const fetchClientDetails = async (clientId) => {
    try {
      const clientResponse = await GetClientById(clientId);

      if (!clientResponse || !clientResponse.data) {
        throw new Error('Invalid client response');
      }

      const clientData = clientResponse.data;

      const primaryAddress = clientData.addresses?.find(addr => addr.addressTypeID === 3);
      const cagingAddress = clientData.addresses?.find(addr => addr.addressTypeID === 2);
      const breAddress = clientData.addresses?.find(addr => addr.addressTypeID === 1);

      const formData = {
        clientId: clientData.clientID,
        clientName: clientData.clientName || '',
        type: clientData.clientTypeID?.toString() || '1',
        id: clientData.clientCode || '',
        abbreviation: clientData.clientCode || '',
        clientAddress: primaryAddress?.addressLine || '',
        city: primaryAddress?.city || '',
        state: mapStateIDToAbbreviation(primaryAddress?.stateID) || '',
        zip: primaryAddress?.zipCode || '',
        phone: clientData.clientPhoneNumber || '',
        email: clientData.clientEmailAddress || '',
        active: clientData.isActive !== undefined ? clientData.isActive : true,

        organizationName: clientData.cagingOrgName || '',
        poBoxNumber: cagingAddress?.poBoxAddress || '',
        cagingCity: cagingAddress?.city || '',
        cagingState: mapStateIDToAbbreviation(cagingAddress?.stateID) || '',
        cagingZip: cagingAddress?.zipCode || '',

        breOrganizationName: clientData.breOrgName || '',
        brePoBoxNumber: breAddress?.poBoxAddress || '',
        breCity: breAddress?.city || '',
        breState: mapStateIDToAbbreviation(breAddress?.stateID) || '',
        breZip: breAddress?.zipCode || '',

        npAuthorizationNumber: clientData.npAuthorizationNumber || '',
        crid: clientData.crid || '',
        mid: clientData.mid || '',
        permitNumber: clientData.permitNumber || '',

        accountManager: clientData.accountManagerID?.toString() || '',
        productManager: clientData.productionManagerID?.toString() || '',
        listManager: clientData.listManagerID?.toString() || '',
        consultant: clientData.consultantID?.toString() || '',
        creativeDirector: clientData.creativeDirectorID?.toString() || '',

        accountingtype: clientData.accountingDetail?.accountingTypeID?.toString() || '',
        creativefee: clientData.accountingDetail?.creativeFee?.toString() || '',
        housefee: clientData.accountingDetail?.houseFee?.toString() || '',
        salesCommission: clientData.accountingDetail?.salesCommission?.toString() || '',
        flatfee: clientData.accountingDetail?.flatFee?.toString() || '',
        monthlyRetainer: clientData.accountingDetail?.monthlyRetainer?.toString() || '',
        quantityPerPrice: clientData.accountingDetail?.quantityPerPiece?.toString() || '',
        sageClientCode: clientData.accountingDetail?.sageClientCode || '',
        designFee: clientData.accountingDetail?.designFee?.toString() || '',
       
        contractDate: clientData.accountingDetail?.contractDate
          ? clientData.accountingDetail.contractDate.split('T')[0]
          : '',

        isThankYouProgramActive: clientData.isThankYouProgramActive !== undefined ? clientData.isThankYouProgramActive : true,
       
        clientContractInfo: clientData.clientContractInfo || '',

       
        primaryAddressID: primaryAddress?.clientAddressID || 0,
        cagingAddressID: cagingAddress?.clientAddressID || 0,
        breAddressID: breAddress?.clientAddressID || 0,
        clientAccountingDetailID: clientData.accountingDetail?.clientAccountingDetailID || 0,
      };

      return formData;
    } catch (error) {
      console.error('Error fetching client details:', error);
      throw error;
    }
  };

  
  const SectionHeader = ({ label }) => {
    return (
      <div
        style={{
          gridColumn: '1 / -1',
          marginTop: '10px',
          marginBottom: '10px',
          fontSize: '20px',
          fontWeight: '600',
          color: '#000000',
          width: '100%',
          position: 'relative'
        }}
      >
        {label}
        <div
          style={{
            position: 'absolute',
            bottom: '-2px',
            left: '0',
            width: '100px',
            height: '2px',
          }}
        />
      </div>
    );
  };

  // ===== Validation helpers =====
  const validatePhone = (phone) => {
    if (!phone) return true;
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
    return phoneRegex.test(phone);
  };

  const validateEmail = (email) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateZip = (zip) => {
    if (!zip) return true;
    const zipRegex = /^\d{5}(-\d{4})?$/;
    return zipRegex.test(zip);
  };

  const validateCurrency = (value) => {
    if (!value) return true;
    
    const currencyRegex = /^\$?\d{1,6}(\.\d{1,5})?$/;
    return currencyRegex.test(value);
  };

  const validateAddressFields = (formData) => {
    const errors = {};

    const hasPrimaryAddress = formData.clientAddress || formData.city || formData.state || formData.zip;
    if (hasPrimaryAddress) {
      if (!formData.city) errors.city = 'City is required when address is provided';
      if (!formData.state) errors.state = 'State is required when address is provided';
      if (!formData.zip) {
        errors.zip = 'ZIP code is required when address is provided';
      } else if (!validateZip(formData.zip)) {
        errors.zip = 'Please enter a valid ZIP code ';
      }
    }

    const hasCagingAddress = formData.organizationName || formData.poBoxNumber || formData.cagingCity || formData.cagingState || formData.cagingZip;
    if (hasCagingAddress) {
      if (!formData.cagingCity) errors.cagingCity = 'City is required for Caging Address';
      if (!formData.cagingState) errors.cagingState = 'State is required for Caging Address';
      if (!formData.cagingZip) {
        errors.cagingZip = 'ZIP code is required for Caging Address';
      } else if (!validateZip(formData.cagingZip)) {
        errors.cagingZip = 'Please enter a valid ZIP code';
      }
    }

    const hasBREAddress = formData.breOrganizationName || formData.brePoBoxNumber || formData.breCity || formData.breState || formData.breZip;
    if (hasBREAddress) {
      if (!formData.breCity) errors.breCity = 'City is required for BRE Address';
      if (!formData.breState) errors.breState = 'State is required for BRE Address';
      if (!formData.breZip) {
        errors.breZip = 'ZIP code is required for BRE Address';
      } else if (!validateZip(formData.breZip)) {
        errors.breZip = 'Please enter a valid ZIP code';
      }
    }

    return errors;
  };

  const validateClientFields = (formData, isEdit = false) => {
    const errors = {};

    if (!formData.clientName || formData.clientName.trim() === '') {
      errors.clientName = 'Client Name is required';
    }

    if (!formData.type || formData.type === '') {
      errors.type = 'Please select a Client Type';
    }

    if (!formData.id || formData.id.trim() === '') {
      errors.id = 'ID is required';
    } else {
      const alphanumericRegex = /^[a-zA-Z0-9]+$/;
      if (!alphanumericRegex.test(formData.id)) {
        errors.id = 'ID must be alphanumeric (letters and numbers only)';
      }

      if (!isEdit && clientIds.has(formData.id)) {
        errors.id = 'This value already exists. Please enter a unique value.';
      }
    }

    if (formData.email && !validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone || formData.phone.trim() === '') {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }

    const addressErrors = validateAddressFields(formData);
    Object.assign(errors, addressErrors);

    const currencyFields = ['creativefee', 'housefee', 'flatfee', 'monthlyRetainer', 'designFee'];
    currencyFields.forEach(field => {
      if (formData[field] && !validateCurrency(formData[field])) {
        errors[field] = 'Please enter a valid numeric amount';
      }
    });

    if (formData.salesCommission && !/^\d{1,6}(\.\d{1,5})?%?$/.test(formData.salesCommission)) {
      errors.salesCommission = 'Please enter a valid number (up to 6 digits before decimal, 5 after)';
    }

    if (!formData.quantityPerPrice || formData.quantityPerPrice.toString().trim() === '') {
      errors.quantityPerPrice = 'Quantity Per Piece is required';
    } else if (!/^\d+$/.test(formData.quantityPerPrice)) {
      errors.quantityPerPrice = 'Quantity Per Piece must be a whole number (integers only)';
    }

    return errors;
  };

  const handleActionClick = (event, client) => {
    event.stopPropagation();

    if (currentUser.role !== 'Admin') {
      showErrorMessage('You do not have permission to perform this action.');
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    setActionMenu({
      isOpen: true,
      clientId: client.clientId,
      clientData: client,
      position: {
        x: rect.left - 40,
        y: rect.bottom + window.scrollY
      }
    });
  };

  const handleEditFromMenu = async () => {
    if (!actionMenu.clientId) return;

    if (currentUser.role !== 'Admin') {
      showErrorMessage('Only Admin users can edit clients.');
      return;
    }

    try {
      setEditLoading(true);

      const clientDetails = await fetchClientDetails(actionMenu.clientId);

      setModalData(clientDetails);
      setModalMode('edit');
      setIsModalOpen(true);
      setValidationErrors({});
      setActionMenu({ isOpen: false, clientId: null, clientData: null, position: { x: 0, y: 0 } });

    } catch (error) {
      console.error('Error preparing edit:', error);
      showErrorMessage(error.message || 'Failed to load client data for editing.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteFromMenu = async () => {
    if (!actionMenu.clientId || !actionMenu.clientData) return;

    if (currentUser.role !== 'Admin') {
      showErrorMessage('Only Admin users can delete clients.');
      return;
    }

    // ✅ open custom modal instead of alert
    setClientToDelete(actionMenu.clientData);
    setShowDeleteConfirm(true);

    // close dropdown menu
    setActionMenu({ isOpen: false, clientId: null, clientData: null, position: { x: 0, y: 0 } });

    return;
  };

  const handleCancelDelete = () => {
    if (deleteInProgress) return;
    setShowDeleteConfirm(false);
    setClientToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return;

    try {
      setDeleteInProgress(true);

      const response = await DeleteClientById(clientToDelete.clientId);

      const isSuccess =
        response?.status === 200 ||
        response?.status === 204 ||
        response?.success === true ||
        response?.data?.success === true ||
        (response && !response.error && !response.errors);

      if (isSuccess) {
        // ✅ remove from grid
        setRowData(prevData =>
          prevData.filter(client =>
            Number(client.clientId) !== Number(clientToDelete.clientId)
          )
        );

        // ✅ update ID set
        setClientIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(clientToDelete.id);
          return newSet;
        });

        // ✅ refresh grid
        if (gridRef.current?.api) {
          setTimeout(() => {
            gridRef.current.api.refreshCells({ force: true });
          }, 100);
        }

        showSuccessMessage(`Client "${clientToDelete.clientName}" has been deleted successfully`);
      } else {
        throw new Error(response?.message || 'Failed to delete client');
      }

    } catch (error) {
      console.error('Delete error:', error);

      let errorMessage = 'Failed to delete client';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showErrorMessage(errorMessage);

    } finally {
      setDeleteInProgress(false);
      setShowDeleteConfirm(false);
      setClientToDelete(null);
    }
  };

  const handleAddClient = () => {
    if (currentUser.role !== 'Admin') {
      showErrorMessage('Only Admin users can create clients.');
      return;
    }

    setModalData({
      clientName: '',
      type: '',
      id: '',
      abbreviation: '',
      clientAddress: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      active: true,
      organizationName: '',
      poBoxNumber: '',
      cagingCity: '',
      cagingState: '',
      cagingZip: '',
      breOrganizationName: '',
      brePoBoxNumber: '',
      breCity: '',
      breState: '',
      breZip: '',
      npAuthorizationNumber: '',
      crid: '',
      mid: '',
      permitNumber: '',
      accountManager: '',
      productManager: '',
      listManager: '',
      consultant: '',
      creativeDirector: '',
      accountingtype: '',
      creativefee: '',
      housefee: '',
      salesCommission: '',
      flatfee: '',
      monthlyRetainer: '',
      quantityPerPrice: '',
      sageClientCode: '',
      designFee: '',
     
      contractDate: '',
      isThankYouProgramActive: false,
    
      clientContractInfo: ''
    });

    setModalMode('add');
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const num = parseFloat(value.replace(/[^0-9.-]+/g, ''));
    if (isNaN(num)) return value;
    return `$${num.toFixed(2)}`;
  };

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;

    let formattedValue = value;

    if (name === 'id') {
     
      const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');

      let errorMsg = '';

      if (!cleanedValue) {
        errorMsg = 'ID is required';
      } else if (clientIds.has(cleanedValue) && modalMode !== 'edit') {
        errorMsg = 'Client ID already exists';
      }

    
      setValidationErrors(prev => ({
        ...prev,
        id: errorMsg || undefined
      }));

      formattedValue = cleanedValue;
    }

    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length <= 10) {
        if (cleaned.length >= 3 && cleaned.length <= 6) {
          formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
        } else if (cleaned.length > 6) {
          formattedValue = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        }
      }
    }



    if (name === 'zip' || name === 'cagingZip' || name === 'breZip') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length <= 5) {
        formattedValue = cleaned;
      } else if (cleaned.length <= 9) {
        formattedValue = `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
      }
    }

   
    if (name === 'quantityPerPrice') {
      formattedValue = value.replace(/\D/g, '');
    }

    setModalData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : formattedValue
    }));

  
    if (name !== 'id') {
      setValidationErrors(prev => {
        if (!prev[name]) return prev;
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSaveClient = async (formData, e = null) => {
    console.log("formData", formData)
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {

      const idRegex = /^[a-zA-Z0-9]+$/;

    
      const newErrors = validateClientFields(formData, modalMode === 'edit');

      if (!formData.id || !idRegex.test(formData.id)) {
        newErrors.id = 'Only letters and numbers allowed. No spaces or special characters.';
      }

      if (Object.keys(newErrors).length > 0) {
        setValidationErrors(newErrors);
        if (!formData.id || !idRegex.test(formData.id)) {
          showErrorMessage('Please enter a valid Client ID');
        }
        suppressNextAutoCloseRef.current = true;
        return false;
      }

   
      setValidationErrors({});

      setIsSaving(true);

      const clientDataForAPI = {
        clientId: modalMode === 'edit' ? formData.clientId : 0,
        clientCode: formData.id?.trim() || '',
        clientTypeID: parseInt(formData.type) || 1,
        clientName: formData.clientName?.trim() || '',
        isActive: formData.active !== undefined ? formData.active : true,
        clientPhoneNumber: formData.phone || '',
        clientEmailAddress: formData.email || '',
        abbreviation: formData.abbreviation || '',
        cagingOrgName: formData.organizationName || '',
        breOrgName: formData.breOrganizationName || '',
        npAuthorizationNumber: formData.npAuthorizationNumber || '',
        crid: formData.crid || '',
        mid: formData.mid || '',
        permitNumber: formData.permitNumber || '',
        accountManagerID: formData.accountManager ? parseInt(formData.accountManager) : null,
        productionManagerID: formData.productManager ? parseInt(formData.productManager) : null,
        listManagerID: formData.listManager ? parseInt(formData.listManager) : null,
        consultantID: formData.consultant ? parseInt(formData.consultant) : null,
        creativeDirectorID: formData.creativeDirector ? parseInt(formData.creativeDirector) : null,
        isThankYouProgramActive: formData.isThankYouProgramActive !== undefined ? formData.isThankYouProgramActive : false,
        // NEW: include clientContractInfo in POST/PUT payload
        clientContractInfo: formData.clientContractInfo?.trim() || '',
        createdByUserID: modalMode === 'add' ? currentUser.userID : 0,
        updatedByUserID: modalMode === 'edit' ? currentUser.userID : 0,
        addresses: [],
        contacts: [],
      };

      const isEdit = modalMode === 'edit';

     
      clientDataForAPI.addresses.push({
        ...(isEdit && { clientAddressID: formData.primaryAddressID || 0 }),
        addressTypeID: 3,
        addressLine: formData.clientAddress || null,
        poBoxAddress: formData.poBoxNumber || '',
        city: formData.city || '',
        stateID: mapAbbreviationToStateID(formData.state),
        zipCode: formData.zip || ''
      });
      if (formData.organizationName || formData.poBoxNumber || formData.cagingCity || formData.cagingState || formData.cagingZip) {
        clientDataForAPI.addresses.push({
          ...(isEdit && { clientAddressID: formData.cagingAddressID || 0 }),
          addressTypeID: 2,
          addressLine: null,
          poBoxAddress: formData.poBoxNumber || '',
          city: formData.cagingCity || '',
          stateID: mapAbbreviationToStateID(formData.cagingState),
          zipCode: formData.cagingZip || ''
        });
      }

      // BRE address (addressTypeID: 1)
      if (formData.breOrganizationName || formData.brePoBoxNumber || formData.breCity || formData.breState || formData.breZip) {
        clientDataForAPI.addresses.push({
          ...(isEdit && { clientAddressID: formData.breAddressID || 0 }),
          addressTypeID: 1,
          addressLine: null,
          poBoxAddress: formData.brePoBoxNumber || '',
          city: formData.breCity || '',
          stateID: mapAbbreviationToStateID(formData.breState),
          zipCode: formData.breZip || ''
        });
      }

      if (modalMode === 'add') {
        clientDataForAPI.addresses = clientDataForAPI.addresses.map(({ clientAddressID, ...rest }) => rest);
      }

      const accountingDetail = {
        ...(isEdit && { clientAccountingDetailID: formData.clientAccountingDetailID || 0 }),
        accountingTypeID: parseInt(formData.accountingtype) || 0,
        creativeFee: parseFloat(formData.creativefee) || 0,
        houseFee: parseFloat(formData.housefee) || 0,
        salesCommission: parseFloat(formData.salesCommission?.replace('%', '')) || 0,
        flatFee: parseFloat(formData.flatfee) || 0,
        monthlyRetainer: parseFloat(formData.monthlyRetainer) || 0,
        quantityPerPiece: parseInt(formData.quantityPerPrice) || 0,
        designFee: parseFloat(formData.designFee) || 0,
        sageClientCode: formData.sageClientCode || '',
        contractDate: formData.contractDate
          ? new Date(formData.contractDate).toISOString()
          : new Date().toISOString()
      };

      if (formData.accountingtype && parseInt(formData.accountingtype) > 0) {
        clientDataForAPI.accountingDetail = accountingDetail;
      } else {
        clientDataForAPI.accountingDetail = null;
      }

      if (!clientDataForAPI.clientCode) {
        throw new Error('Client ID is required');
      }
      if (!clientDataForAPI.clientName) {
        throw new Error('Client Name is required');
      }
      if (!clientDataForAPI.clientTypeID) {
        throw new Error('Client Type is required');
      }

      let response;

      if (modalMode === 'edit' && formData.clientId) {
        try {
          response = await UpdateClientById(formData.clientId, clientDataForAPI);

          if (!response) {
            throw new Error('No response received from server');
          }

          const responseStatus = response.status || response.data?.status;
          const isSuccess = responseStatus === 200 || responseStatus === 204;

          if (isSuccess) {
            try {
              const updatedClientFromServer = await GetClientById(formData.clientId);

              if (updatedClientFromServer && updatedClientFromServer.data) {
                setRowData(prevData => prevData.map(client => {
                  const clientIdNum = parseInt(client.clientId);
                  const updatingIdNum = parseInt(formData.clientId);

                  return clientIdNum === updatingIdNum
                    ? transformClientData(updatedClientFromServer.data)
                    : client;
                }));
              } else {
                const clientTypeLabel = managedClientTypes.find(
                  type => type.value === formData.type
                )?.label || 'N/A';

                const updatedClient = {
                  clientID: parseInt(formData.clientId),
                  clientCode: formData.id,
                  clientName: formData.clientName,
                  clientTypeID: parseInt(formData.type),
                  clientTypeName: clientTypeLabel,
                  isActive: formData.active,
                  clientEmailAddress: formData.email,
                  abbreviation: formData.abbreviation,
                  clientPhoneNumber: formData.phone,
                  addresses: clientDataForAPI.addresses,
                  cagingOrgName: formData.organizationName,
                  breOrgName: formData.breOrganizationName,
                  npAuthorizationNumber: formData.npAuthorizationNumber,
                  crid: formData.crid,
                  mid: formData.mid,
                  permitNumber: formData.permitNumber,
                  accountManagerID: formData.accountManager ? parseInt(formData.accountManager) : null,
                  productionManagerID: formData.productManager ? parseInt(formData.productManager) : null,
                  listManagerID: formData.listManager ? parseInt(formData.listManager) : null,
                  consultantID: formData.consultant ? parseInt(formData.consultant) : null,
                  creativeDirectorID: formData.creativeDirector ? parseInt(formData.creativeDirector) : null,
                  accountingDetail: clientDataForAPI.accountingDetail,
                  isThankYouProgramActive: formData.isThankYouProgramActive,
                  clientContractInfo: formData.clientContractInfo || ''
                };

                setRowData(prevData => prevData.map(client => {
                  const clientIdNum = parseInt(client.clientId);
                  const updatingIdNum = parseInt(formData.clientId);

                  return clientIdNum === updatingIdNum
                    ? transformClientData(updatedClient)
                    : client;
                }));
              }
            } catch (fetchError) {
              console.warn('Failed to fetch updated client from server:', fetchError);

              const clientTypeLabel = managedClientTypes.find(
                type => type.value === formData.type
              )?.label || 'N/A';

              const updatedClient = {
                clientID: parseInt(formData.clientId),
                clientCode: formData.id,
                clientName: formData.clientName,
                clientTypeID: parseInt(formData.type),
                clientTypeName: clientTypeLabel,
                isActive: formData.active,
                clientEmailAddress: formData.email,
                abbreviation: formData.abbreviation,
                clientPhoneNumber: formData.phone,
                addresses: clientDataForAPI.addresses,
                cagingOrgName: formData.organizationName,
                breOrgName: formData.breOrganizationName,
                npAuthorizationNumber: formData.npAuthorizationNumber,
                crid: formData.crid,
                mid: formData.mid,
                permitNumber: formData.permitNumber,
                accountManagerID: formData.accountManager ? parseInt(formData.accountManager) : null,
                productionManagerID: formData.productManager ? parseInt(formData.productManager) : null,
                listManagerID: formData.listManager ? parseInt(formData.listManager) : null,
                consultantID: formData.consultant ? parseInt(formData.consultant) : null,
                creativeDirectorID: formData.creativeDirector ? parseInt(formData.creativeDirector) : null,
                accountingDetail: clientDataForAPI.accountingDetail,
                isThankYouProgramActive: formData.isThankYouProgramActive,
                clientContractInfo: formData.clientContractInfo || ''
              };

              setRowData(prevData => prevData.map(client => {
                const clientIdNum = parseInt(client.clientId);
                const updatingIdNum = parseInt(formData.clientId);

                return clientIdNum === updatingIdNum
                  ? transformClientData(updatedClient)
                  : client;
              }));
            }

            setIsModalOpen(false);
            setValidationErrors({});
            setIsSaving(false);

            showSuccessMessage(`Client "${formData.clientName}" updated successfully!`);

            setTimeout(async () => {
              try {
                await fetchInitialData();
              } catch (refreshError) {
                return refreshError;
              }
            }, 1000);

            return true;

          } else {
            let errorMessage = 'Failed to update client';

            const errorFromData = response.data?.message || response.data?.error;
            const errorFromResponse = response.message || response.error;
            const errorFromStatusText = response.statusText;

            if (errorFromData) {
              errorMessage = errorFromData;
            } else if (errorFromResponse) {
              errorMessage = errorFromResponse;
            } else if (errorFromStatusText && errorFromStatusText !== 'OK') {
              errorMessage = errorFromStatusText;
            } else if (response.data && typeof response.data === 'string') {
              errorMessage = response.data;
            } else if (typeof response === 'string') {
              errorMessage = response;
            }

            throw new Error(errorMessage);
          }

        } catch (error) {
          return error;

          let errorMessage = 'Failed to update client. Please try again.';

          if (error.response) {
            if (error.response.status === 400) {
              errorMessage = 'Bad request. Please check your input data.';
              if (error.response.data) {
                const errorDetails = typeof error.response.data === 'object'
                  ? JSON.stringify(error.response.data)
                  : error.response.data;
                errorMessage += ` Details: ${errorDetails}`;
              }
            } else if (error.response.status === 404) {
              errorMessage = 'Client not found. It may have been deleted.';
            } else if (error.response.status === 403) {
              errorMessage = 'You do not have permission to update this client.';
            } else if (error.response.status === 409) {
              errorMessage = 'Client with this ID already exists.';
            } else if (error.response.status === 500) {
              errorMessage = 'Server error. Please try again later.';
            } else if (error.response.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error.response.data) {
              errorMessage = JSON.stringify(error.response.data);
            }
          } else if (error.request) {
            errorMessage = 'Network error. Please check your connection.';
          } else if (error.message) {
            errorMessage = error.message;
          }

          throw new Error(errorMessage);
        }
      } else {
        // CREATE new client
        try {
          response = await CreateClients(clientDataForAPI);

          if (response?.errors) {
            const firstErrorField = Object.keys(response?.errors)[0];
            if (firstErrorField) {
              const errorValue = response?.errors[firstErrorField];
              const errorMessage = Array.isArray(errorValue) ? errorValue[0] : errorValue;
              setIsSaving(false);
              showErrorMessage(errorMessage);
              suppressNextAutoCloseRef.current = true;
              return false;
            }
          }

          setClientIds(prev => new Set([...prev, formData.id]));

          const createdClientId =
            response?.data?.clientID ||
            response?.clientID ||
            response?.data?.id ||
            response?.id ||
            `temp-${Date.now()}`;

          const newClient = {
            clientID: createdClientId,
            clientCode: formData.id,
            clientName: formData.clientName,
            clientTypeID: parseInt(formData.type),
            clientTypeName: managedClientTypes.find(
              type => type.value === formData.type
            )?.label || 'N/A',
            isActive: formData.active,
            isThankYouProgramActive: formData.isThankYouProgramActive,
            clientContractInfo: formData.clientContractInfo || '',
            accountingDetail: clientDataForAPI.accountingDetail
          };

          setRowData(prevData => [transformClientData(newClient), ...prevData]);

          setIsModalOpen(false);
          setValidationErrors({});
          setIsSaving(false);

          showSuccessMessage(`Client "${formData.clientName}" created successfully!`);

          setTimeout(() => {
            fetchInitialData().catch(e => console.warn('Background refresh failed:', e));
          }, 1000);

          return true;

        } catch (apiError) {
          return apiError;
        }
      }
    } catch (error) {
      return error;
      setIsSaving(false);

      let errorMessage = 'Failed to save client. Please try again.';

      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = 'Bad request. Please check your input.';
        } else if (error.response.status === 409) {
          errorMessage = 'Client already exists with this ID.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      showErrorMessage(errorMessage);
      suppressNextAutoCloseRef.current = true;
      return false;
    }
  };

  const EditIconRenderer = (params) => {
    const client = params.data;

    return (
      <div className='edit-icon-render'>
        <div
          onClick={(e) => handleActionClick(e, client)}
          className='action-icon'
          style={{ cursor: 'pointer' }}
        >
          <Icon
            name="action"
            size={16}
            width={3}
            alt="action"
          />
        </div>
      </div>
    );
  };

  const renderActionMenu = () => {
    if (!actionMenu.isOpen) return null;

    return (
      <div
        ref={actionMenuRef}
        style={{
          position: 'absolute',
          top: actionMenu.position.y,
          left: actionMenu.position.x,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          minWidth: '120px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <button
          onClick={handleEditFromMenu}
          style={{
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: '14px',
            color: '#333',
            borderBottom: '1px solid #eee'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          disabled={deleteLoading || editLoading}
        >
          {editLoading ? 'Loading...' : 'Edit'}
        </button>
        <button
          onClick={handleDeleteFromMenu}
          style={{
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: '14px',
            color: '#e74c3c'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          disabled={deleteLoading}
        >
          {deleteLoading ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    );
  };

  const handleCloseModal = () => {
   
    if (suppressNextAutoCloseRef.current) {
      suppressNextAutoCloseRef.current = false;
      return;
    }

    if (!isSaving) {
      setIsModalOpen(false);
      setValidationErrors({});
    }
  };

 
  const ToggleSwitch = ({ field, formData, onChange }) => {
    const handleToggle = (fieldName) => {
      if (onChange) {
        onChange({
          target: {
            name: fieldName,
            type: 'checkbox',
            checked: !formData[fieldName]
          }
        });
      }
    };

    return (
      <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
        <div style={{ flex: 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#333'
          }}>
            {formData['active'] ? 'Active' : 'Inactive'}
          </label>
          <div
            onClick={() => handleToggle('active')}
            style={{
              width: '50px',
              height: '26px',
              backgroundColor: formData['active'] ? '#0D6EFD' : '#ccc',
              borderRadius: '13px',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background-color 0.3s'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '3px',
              left: formData['active'] ? '27px' : '3px',
              width: '20px',
              height: '20px',
              backgroundColor: 'white',
              borderRadius: '50%',
              transition: 'left 0.3s'
            }} />
          </div>
        </div>
      </div>
    );
  };


  const ThankYouToggleSwitch = ({ field, formData, onChange }) => {
    const handleToggle = (fieldName) => {
      if (onChange) {
        onChange({
          target: {
            name: fieldName,
            type: 'checkbox',
            checked: !formData[fieldName]
          }
        });
      }
    };

    return (
      <div style={{ display: 'flex', gap: '20px', marginTop: '8px', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            onClick={() => handleToggle('isThankYouProgramActive')}
            style={{
              width: '50px',
              height: '26px',
              backgroundColor: formData['isThankYouProgramActive'] ? '#0D6EFD' : '#ccc',
              borderRadius: '13px',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background-color 0.3s'
            }}
          >
            <div style={{
              position: 'absolute',
              top: '3px',
              left: formData['isThankYouProgramActive'] ? '27px' : '3px',
              width: '20px',
              height: '20px',
              backgroundColor: 'white',
              borderRadius: '50%',
              transition: 'left 0.3s'
            }} />
          </div>

          <span style={{
            fontSize: '14px',
            fontWeight: formData['isThankYouProgramActive'] ? '600' : '400',
            color: formData['isThankYouProgramActive'] ? '#0D6EFD' : '#666'
          }}>
            {formData['isThankYouProgramActive'] ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    );
  };


  const TextAreaComponent = ({ field, formData, onChange }) => {
    const error = validationErrors[field.name];
    const maxLength = field.maxLength || 2000;
    const currentLength = (formData[field.name] || '').length;

    return (
      <div style={{ flex: 1, width: '100%' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label}
          {field.required && <span style={{ color: '#e74c3c' }}> *</span>}
        </label>
        <textarea
          name={field.name}
          value={formData[field.name] || ''}
          onChange={onChange}
          placeholder={field.placeholder || 'Enter contract information...'}
          rows={field.rows || 4}
          maxLength={maxLength}
          disabled={field.disabled}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: error ? '1px solid #e74c3c' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'inherit',
            backgroundColor: field.disabled ? '#f5f5f5' : 'white',
            boxSizing: 'border-box',
            resize: 'vertical',
            minHeight: '100px'
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '4px'
        }}>
          {error ? (
            <div style={{ color: '#e74c3c', fontSize: '12px' }}>{error}</div>
          ) : <span />}
          <div style={{ fontSize: '12px', color: '#888' }}>
            {currentLength}/{maxLength}
          </div>
        </div>
      </div>
    );
  };

  const StateDropdown = ({ field, formData, onChange }) => {
    return (
      <div style={{ flex: 0.5 }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label || 'State'}
        </label>
        <select
          name={field.name}
          value={formData[field.name] || ''}
          onChange={onChange}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: validationErrors[field.name] ? '1px solid #e74c3c' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: 'white',
            boxSizing: 'border-box'
          }}
          disabled={field.disabled}
        >
          {stateOptions.map((option, index) => (
            <option
              key={`${field.name}-${index}`}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        {validationErrors[field.name] && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
            {validationErrors[field.name]}
          </div>
        )}
      </div>
    );
  };

  const ValidatedInputWithSuffix = ({ field, formData, onChange, suffix = 'M' }) => {
    const error = validationErrors[field.name];

    const handleChange = (e) => {
      const { value } = e.target;

     

      const regex = /^\d{0,6}(\.\d{0,5})?$/;

      if (value === '' || regex.test(value)) {
      
        if (field.maxValue !== undefined && value !== '') {
          const numericValue = parseFloat(value);
          if (!isNaN(numericValue) && numericValue > field.maxValue) {
            return; 
          }
        }
        onChange(e);
      }
     
    };

    return (
      <div style={{ flex: field.style?.flex || 1, position: 'relative' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label}{field.required && <span style={{ color: '#e74c3c' }}> *</span>}
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            inputMode="decimal"
            name={field.name}
            value={formData[field.name] || ''}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '8px 12px',
              paddingRight: '30px',
              border: error ? '1px solid #e74c3c' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: field.disabled ? '#f5f5f5' : 'white',
              boxSizing: 'border-box'
            }}
            disabled={field.disabled}
            placeholder={field.placeholder || ''}
          />
          <span style={{
            padding: "8px 12px 8px 12px",
            position: 'absolute',
            backgroundColor: "#CECECE",
            fontSize: "14px",
            right: '0px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#666',
            fontWeight: '500',
            pointerEvents: 'none'
          }}>
            {suffix}
          </span>
        </div>
        {error && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };


  const ValidatedInput = ({ field, formData, onChange }) => {
    const error = validationErrors[field.name];

    return (
      <div style={{ flex: field.style?.flex || 1 }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label}{field.required && <span style={{ color: '#e74c3c' }}> *</span>}
        </label>
        <input
          type={field.inputType || 'text'}
          name={field.name}
          value={formData[field.name] || ''}
          onChange={onChange}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: error ? '1px solid #e74c3c' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: field.disabled ? '#f5f5f5' : 'white',
            boxSizing: 'border-box'
          }}
          disabled={field.disabled}
          placeholder={field.placeholder || ''}
        />
        {error && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };


  const ContractDateInput = ({ field, formData, onChange }) => {
    const error = validationErrors[field.name];

    return (
      <div style={{ flex: field.style?.flex || 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#333',
            whiteSpace: 'nowrap',
            minWidth: '200px'
          }}>
            {field.label}{field.required && <span style={{ color: '#e74c3c' }}> *</span>}
          </label>
          <input
            type={field.inputType || 'date'}
            name={field.name}
            value={formData[field.name] || ''}
            onChange={onChange}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: error ? '1px solid #e74c3c' : '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: field.disabled ? '#f5f5f5' : 'white',
              boxSizing: 'border-box'
            }}
            disabled={field.disabled}
            placeholder={field.placeholder || ''}
          />
        </div>
        {error && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', marginLeft: '126px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };

 
  const ValidatedAlphanumericInput = ({ field, formData, onChange }) => {
    const error = validationErrors[field.name];

    const handleChange = (e) => {
      const { value } = e.target;
    
      const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
      onChange({
        target: {
          name: field.name,
          type: 'text',
          value: cleaned
        }
      });
    };

   
    const handleKeyDown = (e) => {
      const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
      if (allowedControlKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
      if (!/^[a-zA-Z0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <div style={{ flex: field.style?.flex || 1 }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label}{field.required && <span style={{ color: '#e74c3c' }}> *</span>}
        </label>
        <input
          type="text"
          name={field.name}
          value={formData[field.name] || ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          maxLength={field.maxLength}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: error ? '1px solid #e74c3c' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: field.disabled ? '#f5f5f5' : 'white',
            boxSizing: 'border-box'
          }}
          disabled={field.disabled}
          placeholder={field.placeholder || ''}
        />
        {error && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };

 
  const ValidatedIntegerInput = ({ field, formData, onChange }) => {
    const error = validationErrors[field.name];

    const handleChange = (e) => {
      const { value } = e.target;
     
      const digitsOnly = value.replace(/\D/g, '');
      onChange({
        target: {
          name: field.name,
          type: 'text',
          value: digitsOnly
        }
      });
    };

  
    const handleKeyDown = (e) => {
      const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
      if (allowedControlKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    };

    return (
      <div style={{ flex: field.style?.flex || 1 }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label}{field.required && <span style={{ color: '#e74c3c' }}> *</span>}
        </label>
        <input
          type="text"
          inputMode="numeric"
          name={field.name}
          value={formData[field.name] || ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: error ? '1px solid #e74c3c' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: field.disabled ? '#f5f5f5' : 'white',
            boxSizing: 'border-box'
          }}
          disabled={field.disabled}
          placeholder={field.placeholder || 'Enter a whole number'}
        />
        {error && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };

 
  const ValidatedPhoneInput = ({ field, formData, onChange }) => {
    const error = validationErrors[field.name];

    const formatPhone = (digits) => {
      if (digits.length > 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      } else if (digits.length > 3) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else if (digits.length > 0) {
        return `(${digits}`;
      }
      return '';
    };

    const handleChange = (e) => {
      const { value } = e.target;
     
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      onChange({
        target: {
          name: field.name,
          type: 'text',
          value: formatPhone(digitsOnly)
        }
      });
    };

    const handleKeyDown = (e) => {
      const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
      if (allowedControlKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;

      const currentDigitCount = (formData[field.name] || '').replace(/\D/g, '').length;
     
      if (!/^\d$/.test(e.key) || currentDigitCount >= 10) {
        e.preventDefault();
      }
    };

    return (
      <div style={{ flex: field.style?.flex || 1, width: field.width }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label}{field.required && <span style={{ color: '#e74c3c' }}> *</span>}
        </label>
        <input
          type="text"
          inputMode="numeric"
          name={field.name}
          value={formData[field.name] || ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: error ? '1px solid #e74c3c' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: field.disabled ? '#f5f5f5' : 'white',
            boxSizing: 'border-box'
          }}
          disabled={field.disabled}
          placeholder={field.placeholder || '(555) 123-4567'}
        />
        {error && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };

  const ValidatedSelect = ({ field, formData, onChange }) => {
    const error = validationErrors[field.name];

    return (
      <div style={{ flex: field.style?.flex || 1 }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: '#333'
        }}>
          {field.label}{field.required && <span style={{ color: '#e74c3c' }}> *</span>}
        </label>
        <select
          name={field.name}
          value={formData[field.name] || ''}
          onChange={onChange}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: error ? '1px solid #e74c3c' : '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px',
            backgroundColor: field.disabled ? '#f5f5f5' : 'white',
            boxSizing: 'border-box'
          }}
          disabled={field.disabled}
        >
          {field.options.map((option, index) => (
            <option
              key={`${field.name}-${index}`}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px' }}>
            {error}
          </div>
        )}
      </div>
    );
  };

  // Modal fields configuration
  const clientModalFields = [
    [
      {
        type: 'custom',
        name: 'clientName',
        label: 'Client Name',
        component: ValidatedInput,
        required: true,
        className: 'full-width',
        maxLength: 50,
      }
    ],
    [
      {
        type: 'custom',
        name: 'type',
        label: 'Type',
        component: ValidatedSelect,
        required: true,
        options: managedClientTypes,
      },
      {
        type: 'custom',
        name: 'id',
        label: 'Client ID',
        component: ValidatedAlphanumericInput,
        required: true,
        disabled: modalMode === 'edit',
        maxLength: 20
      },
    ],
    [
      {
        type: 'custom',
        name: 'clientAddress',
        label: 'Client Address',
        component: ValidatedInput,
        required: true,
        className: 'full-width'
      }
    ],
    [
      {
        type: 'custom',
        name: 'city',
        label: 'City',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 }
      },
      {
        type: 'custom',
        name: 'state',
        label: 'State',
        component: StateDropdown,
        style: { flex: 0.5 },
        required: true,
      },
      {
        type: 'custom',
        name: 'zip',
        label: 'Zip Code',
        maxLength: 10,
        component: ValidatedInput,
        required: true,
        style: { flex: 1 }
      }
    ],
    [
      {
        type: 'custom',
        name: 'phone',
        label: 'Phone',
        component: ValidatedPhoneInput,
        required: true,
        width: "80%"
      },
      {
        type: 'custom',
        name: 'email',
        label: 'Email',
        component: ValidatedInput,
        required: true,
        width: "80%"
      }
    ],
    [
      {
        type: 'custom',
        name: 'toggleSectionBottom',
        component: ToggleSwitch,
        className: 'full-width',
        style: { marginTop: '20px' }
      }
    ],
    [
      {
        type: 'custom',
        component: () => <SectionHeader label="Caging PO Box Address" />,
        className: 'full-width'
      }
    ],
    [
      {
        type: 'custom',
        name: 'organizationName',
        label: 'Organization Name',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 },
      },
      {
        type: 'custom',
        name: 'poBoxNumber',
        label: 'PO Box Number',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 },
      },
    ],
    [
      {
        type: 'custom',
        name: 'cagingCity',
        label: 'City',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 }
      },
      {
        type: 'custom',
        name: 'cagingState',
        label: 'State',
        component: StateDropdown,
        style: { flex: 0.5 },
        required: true,
      },
      {
        type: 'custom',
        name: 'cagingZip',
        label: 'Zip',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 },
      }
    ],
    [
      {
        type: 'custom',
        component: () => <SectionHeader label="BRE PO Box Address " />,
        className: 'full-width'
      }
    ],
    [
      {
        type: 'custom',
        name: 'breOrganizationName',
        label: 'Organization Name',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 },
      },
      {
        type: 'custom',
        name: 'brePoBoxNumber',
        label: 'PO Box Number',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 }
      }
    ],
    [
      {
        type: 'custom',
        name: 'breCity',
        label: 'City',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 }
      },
      {
        type: 'custom',
        name: 'breState',
        label: 'State',
        component: StateDropdown,
        style: { flex: 0.5 },
        required: true,
      },
      {
        type: 'custom',
        name: 'breZip',
        label: 'Zip',
        component: ValidatedInput,
        required: true,
        style: { flex: 1 }
      }
    ],
    [
      {
        type: 'custom',
        component: () => <SectionHeader label="USPS" />,
        className: 'full-width'
      }
    ],
    [
      {
        type: 'custom',
        name: 'npAuthorizationNumber',
        label: 'NP Authorization Number',
        component: ValidatedInput,
        required: true,
      }
    ],
    [
      {
        type: 'custom',
        name: 'crid',
        label: 'CRID',
        component: ValidatedInput,
        required: true,
      }
    ],
    [
      {
        type: 'custom',
        name: 'mid',
        label: 'MID',
        component: ValidatedInput,
        required: true,
      }
    ],
    [
      {
        type: 'custom',
        name: 'permitNumber',
        label: 'Permit Number',
        component: ValidatedInput,
        required: true,
      }
    ],
    [
      {
        type: 'custom',
        component: () => <SectionHeader label="Internal Roles " />,
        className: 'full-width'
      }
    ],
    [
      {
        type: 'custom',
        name: 'accountManager',
        label: 'Account Manager',
        component: ValidatedSelect,
        required: true,
        options: accountManagers,
      }
    ],
    [
      {
        type: 'custom',
        name: 'productManager',
        label: 'Product Manager',
        component: ValidatedSelect,
        required: true,
        options: productManagers,
      }
    ],
    [
      {
        type: 'custom',
        name: 'listManager',
        label: 'List Manager',
        component: ValidatedSelect,
        required: true,
        options: listManagers,
      }
    ],
    [
      {
        type: 'custom',
        name: 'consultant',
        label: 'Consultant',
        component: ValidatedSelect,
        required: true,
        options: consultants,
      }
    ],
    [
      {
        type: 'custom',
        name: 'creativeDirector',
        label: 'Creative Director',
        component: ValidatedSelect,
        required: true,
        options: creativeDirector,
      }
    ],
    [
      {
        type: 'custom',
        component: () => <SectionHeader label="Accounting Details" />,
        className: 'full-width'
      }
    ],
    [
      {
        type: 'custom',
        name: 'accountingtype',
        label: 'Accounting Type',
        component: ValidatedSelect,
        required: true,
        options: accountingTypes,
        style: { flex: 1 }
      },
      {
        type: 'custom',
        name: 'creativefee',
        label: 'Creative Fee ($)',
        component: ValidatedInputWithSuffix,
        suffix: '$',
        required: true,
        style: { flex: 1 },
      },
      {
        type: 'custom',
        name: 'housefee',
        label: 'House Fee ($)',
        component: ValidatedInputWithSuffix,
        required: true,
        style: { flex: 1 },
      }
    ],
    [
      {
        type: 'custom',
        name: 'salesCommission',
        label: 'Sales Commission',
        component: ValidatedInputWithSuffix,
        suffix: '%',
        required: true,
        maxValue: 100,
        style: { flex: 1 },
      },
      {
        type: 'custom',
        name: 'flatfee',
        label: 'Flat Fee ($)',
        component: ValidatedInputWithSuffix,
        suffix: '$',
        required: true,
        style: { flex: 1 },
      },
      {
        type: 'custom',
        name: 'monthlyRetainer',
        label: 'Monthly Retainer ($)',
        component: ValidatedInputWithSuffix,
        suffix: '$',
        required: true,
        style: { flex: 1 },
      }
    ],
    [
      {
        type: 'custom',
        name: 'quantityPerPrice',
        label: 'Quantity Per Piece',
        component: ValidatedIntegerInput,
        required: true,
      },
      {
        type: 'custom',
        name: 'sageClientCode',
        label: 'Sage Client Code',
        component: ValidatedInput,
        required: true,
      },
      {
        type: 'custom',
        name: 'designFee',
        label: 'Design Fee ($)',
        component: ValidatedInputWithSuffix,
        suffix: '$',
        required: true,
      },
    ],
   
    [
      {
        type: 'custom',
        name: 'contractDate',
        label: 'Contract Date',
        component: ContractDateInput,
        inputType: 'date',
        required: false,
        style: { flex: 1 },
      }
    ],
   
    [
      {
        type: 'custom',
        component: () => <SectionHeader label="Contract Information" />,
        className: 'full-width'
      }
    ],
    [
      {
        type: 'custom',
        name: 'clientContractInfo',
        component: TextAreaComponent,
        className: 'full-width',
        rows: 5,
        maxLength: 2000,
        placeholder: 'Please leave notes regarding the contract information...',
      }
    ]
  ];

  const [columnDefs] = useState([
    {
      headerName: 'ID',
      field: 'Id',
      sortable: true,
      filter: 'agTextColumnFilter',
      width: 120,
      maxWidth: 120,
      minWidth: 120,
      suppressSizeToFit: true,
    },
    {
      headerName: 'Client Code',
      field: 'clientNumber',
      sortable: true,
      minWidth: 200,
      flex: 1,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Client Name',
      field: 'clientName',
      sortable: true,
      minWidth: 250,
      flex: 1,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Client Type',
      field: 'clientType',
      sortable: true,
      width: 180,
      maxWidth: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
    },
    {
      headerName: 'Status',
      field: 'status',
      sortable: true,
      width: 180,
      maxWidth: 180,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      cellRenderer: StatusCell,
      cellRendererParams: {
        active: '/assets/active-icon.svg'
      }
    },
    {
      headerName: 'Action',
      field: 'edit',
      filter: false,
      width: 180,
      sortable: false,
      maxWidth: 180,
      minWidth: 180,
      suppressSizeToFit: true,
      cellRenderer: EditIconRenderer,
      cellStyle: {
        textAlign: 'center',
      }
    }
  ]);

  const modalProps = {
    isOpen: isModalOpen,
    onClose: handleCloseModal,
    onSave: handleSaveClient,
    title: modalMode === 'add' ? "Create Client" : "Edit Client",
    fields: clientModalFields,
    initialData: modalData,
    submitText: isSaving ? "Saving..." : modalMode === 'add' ? "Create Client" : "Update Client",
    cancelText: "Cancel",
    width: "800px",
    style: {
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    disableSubmit: isSaving,
    onFieldChange: handleFieldChange
  };

  if (loading && rowData.length === 0) {
    return (
      <AisScreenLoader />
    );
  }

  return (
    <div className="client-maintenance" style={{ marginBottom: "50px" }}>
      <div className="d-flex justify-content-between align-items-center mb-4 position-absolute gap-3">
        <button
          onClick={handleAddClient}
          className='add-client-btn'
          disabled={currentUser.role !== 'Admin'}
          style={{ opacity: currentUser.role !== 'Admin' ? 0.5 : 1 }}
        >
          <span>+</span> Add Client
        </button>

        <div style={{ flex: 1 }}></div>
      </div>

      <div style={{ marginTop: '60px', maxHeight: "500px" }}>
        {rowData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            No client data available. {currentUser.role === 'Admin' ? 'Click "Add Client" to create your first client.' : 'Contact an administrator to add clients.'}
          </div>
        ) : (
          <AgGrid
            ref={gridRef}
            searchPlaceholder="Search clients..."
            enableSearch={true}
            enableSorting={true}
            enableFilter={true}
            pagination={true}
            height="500px"
            hideExportButton={true}
            hideSelectAll={true}
            hideSideBar={true}
            quickFilterText={searchText}
            onQuickFilterChange={setSearchText}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowClicked={(params) => {
              console.log('Row clicked:', params.data);
            }}
            gridStyle={{
              width: '100%',
              height: '500px'
            }}
          />
        )}
      </div>

      <CommonModal {...modalProps} />

      {showDeleteConfirm && (
        <div className="delete-confirmation-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="delete-confirmation-modal" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginBottom: '15px' }}>Confirm Delete</h3>

            <p style={{ marginBottom: '20px' }}>
              Are you sure you want to delete client{" "}
              {clientToDelete?.clientName}?
            </p>

            <div className="delete-confirmation-buttons" style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                className="cancel-delete-btn"
                onClick={handleCancelDelete}
                disabled={deleteInProgress}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>

              <button
                className="confirm-delete-btn"
                onClick={handleConfirmDelete}
                disabled={deleteInProgress}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: deleteInProgress ? 'not-allowed' : 'pointer'
                }}
              >
                {deleteInProgress ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {renderActionMenu()}

      <SuccessMessage
        message={messageState.message}
        type={messageState.type}
        onClose={handleCloseMessage}
      />
    </div>
  );
};

export default ClientMaintenance;
